/**
 * GradeTrackerCard — Settings card where users record their current and
 * target grade per subject. Drives:
 *   - The "Lift X from 76% → 85% 📈" fallback action on Dashboard.
 *   - Grade-weighted system prompts in /api/ai/study-buddy/chat.
 *
 * Backend: GET /api/grades, PUT /api/grades/:subject_id, DELETE /api/grades/:subject_id
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { TrendingUp, Loader2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

const clampGrade = (n) => Math.max(0, Math.min(100, parseInt(n || 0, 10)));

export default function GradeTrackerCard() {
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [draft, setDraft] = useState({ subject_id: '', current_grade: 75, target_grade: 90 });

  const load = async () => {
    setLoading(true);
    try {
      const [subjectsRes, gradesRes] = await Promise.all([
        api.get(apiUrl('/subjects')),
        api.get(apiUrl('/grades')),
      ]);
      setSubjects(subjectsRes.data || []);
      setGrades(gradesRes.data?.grades || []);
    } catch {
      // fall through with empties
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Subjects the user hasn't entered a grade for yet
  const unrecordedSubjects = useMemo(() => {
    const recorded = new Set(grades.map((g) => g.subject_id));
    return subjects.filter((s) => !recorded.has(s.id));
  }, [subjects, grades]);

  const upsertGrade = async (subjectId, subjectName, current, target) => {
    setSaving(subjectId);
    try {
      await api.put(apiUrl(`/grades/${subjectId}`), {
        subject_name: subjectName,
        current_grade: clampGrade(current),
        target_grade: clampGrade(target),
      });
      await load();
      toast.success('Grade saved');
    } catch {
      toast.error('Could not save grade');
    } finally {
      setSaving(null);
    }
  };

  const deleteGrade = async (subjectId) => {
    setSaving(subjectId);
    try {
      await api.delete(apiUrl(`/grades/${subjectId}`));
      await load();
      toast.success('Removed');
    } catch {
      toast.error('Could not remove');
    } finally {
      setSaving(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="grade-tracker-card"
      id="grades"
    >
      <Card className="f500-card p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[var(--isp-glass-border)]">
          <TrendingUp className="w-5 h-5 text-[var(--isp-heading)]" strokeWidth={1.75} />
          <div>
            <h2 className="f500-h2 text-xl">Grade Tracker</h2>
            <p className="text-sm text-[var(--isp-body)] mt-1" style={{ fontFamily: 'var(--sf-text)' }}>
              Tell us your current and target grade per subject. The AI Tutor will weight weak classes.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--isp-body)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Existing grade rows */}
            {grades.length > 0 ? (
              <div className="border border-[var(--isp-glass-border)]">
                <div className="hidden sm:grid grid-cols-[1.5fr_1fr_1fr_auto] gap-3 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)] border-b border-[var(--isp-glass-border)]">
                  <div>Subject</div><div>Current</div><div>Target</div><div></div>
                </div>
                {grades.map((g) => (
                  <GradeRow
                    key={g.subject_id}
                    grade={g}
                    saving={saving === g.subject_id}
                    onSave={(cur, tgt) => upsertGrade(g.subject_id, g.subject_name, cur, tgt)}
                    onDelete={() => deleteGrade(g.subject_id)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--isp-muted)] italic">
                No grades tracked yet — add one below.
              </p>
            )}

            {/* Add new */}
            {unrecordedSubjects.length > 0 && (
              <div className="border border-[var(--isp-glass-border)] p-4 bg-[var(--isp-glass-inner)]" data-testid="grade-tracker-add">
                <div className="grid grid-cols-1 sm:grid-cols-[1.5fr_1fr_1fr_auto] gap-3 items-end">
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Subject</label>
                    <select
                      value={draft.subject_id}
                      onChange={(e) => setDraft({ ...draft, subject_id: e.target.value })}
                      className="mt-1 w-full px-2 py-1.5 bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] text-sm text-[var(--isp-heading)]"
                      data-testid="grade-add-subject"
                    >
                      <option value="">Select…</option>
                      {unrecordedSubjects.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Current %</label>
                    <Input
                      type="number" min={0} max={100}
                      value={draft.current_grade}
                      onChange={(e) => setDraft({ ...draft, current_grade: e.target.value })}
                      className="mt-1 text-sm" data-testid="grade-add-current"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Target %</label>
                    <Input
                      type="number" min={0} max={100}
                      value={draft.target_grade}
                      onChange={(e) => setDraft({ ...draft, target_grade: e.target.value })}
                      className="mt-1 text-sm" data-testid="grade-add-target"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const subj = subjects.find((s) => s.id === draft.subject_id);
                      if (!subj) return toast.error('Pick a subject first');
                      upsertGrade(subj.id, subj.name, draft.current_grade, draft.target_grade);
                      setDraft({ subject_id: '', current_grade: 75, target_grade: 90 });
                    }}
                    className="bg-[#002FA7] hover:bg-[#00227A] text-white text-sm h-9"
                    data-testid="grade-add-submit"
                    disabled={!draft.subject_id || saving === 'new'}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </motion.div>
  );
}

function GradeRow({ grade, saving, onSave, onDelete }) {
  const [cur, setCur] = useState(grade.current_grade);
  const [tgt, setTgt] = useState(grade.target_grade);
  const dirty = clampGrade(cur) !== grade.current_grade || clampGrade(tgt) !== grade.target_grade;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-[1.5fr_1fr_1fr_auto] gap-3 px-4 py-3 items-center border-b last:border-b-0 border-[var(--isp-glass-border)]"
      data-testid={`grade-row-${grade.subject_id}`}
    >
      <div className="text-sm font-semibold text-[var(--isp-heading)] truncate" style={{ fontFamily: 'var(--sf-text)' }}>
        {grade.subject_name}
      </div>
      <Input
        type="number" min={0} max={100}
        value={cur}
        onChange={(e) => setCur(e.target.value)}
        className="text-sm h-8" data-testid={`grade-cur-${grade.subject_id}`}
      />
      <Input
        type="number" min={0} max={100}
        value={tgt}
        onChange={(e) => setTgt(e.target.value)}
        className="text-sm h-8" data-testid={`grade-tgt-${grade.subject_id}`}
      />
      <div className="flex items-center gap-1 justify-end">
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[var(--isp-muted)]" />}
        {dirty && (
          <Button
            size="sm" onClick={() => onSave(cur, tgt)} disabled={saving}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-8 px-2"
            data-testid={`grade-save-${grade.subject_id}`}
          >Save</Button>
        )}
        <button
          onClick={onDelete}
          disabled={saving}
          className="p-1.5 text-[var(--isp-muted)] hover:text-red-500 transition-colors"
          title="Remove" data-testid={`grade-delete-${grade.subject_id}`}
          aria-label={`Remove grade for ${grade.subject_name}`}
        ><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}
